<template>
  <div
    v-if="visible"
    :id="CROSS_DOMAIN_TRANSITION_MODAL_ID"
    class="cross-domain-transition-modal-overlay"
    aria-hidden="false"
  >
    <div class="cross-domain-transition-modal">
      <div class="cross-domain-transition-modal__info">
        <div class="cross-domain-transition-modal__logos">
          <img :src="originLogo" alt="Origin Logo" class="cross-domain-transition-modal__logo" />
          <div ref="arrowsRef" class="cross-domain-transition-modal__arrows" aria-hidden="true" />
          <img
            :src="destinationLogo"
            alt="Destination Logo"
            class="cross-domain-transition-modal__logo"
          />
        </div>

        <div class="cross-domain-transition-modal__text">
          <p class="cross-domain-transition-modal__title" v-html="titleHtml" />

          <p class="cross-domain-transition-modal__subtitle">
            {{ translate('weSmartLinkingRedirectionSubtitle') }}
          </p>
        </div>
      </div>

      <div class="cross-domain-transition-modal__image-wrapper">
        <img class="cross-domain-transition-modal__image" :src="resolvedImageSrc" :alt="imageAlt" />
      </div>
    </div>
  </div>
</template>

<script>
import { buildImageUrl } from '@/infrastructure/Utils'
import { useCrossDomainNavigation } from '@/domain/CrossDomainNavigation'
import lottie from 'lottie-web/build/player/lottie_light'
import animationData from './Arrows.json'

const LOCAL_RHG_LOGO_URL =
  'https://statics.radissonhotels.com/main/img/logo/radisson/rhg-logotype.svg'
const LOCAL_LHG_LOGO_URL = 'https://statics.louvrehotels.com/main/img/logo/louvre/lhg-logotype.svg'

const CROSS_DOMAIN_TRANSITION_MODAL_ID = 'cross-domain-transition-modal'

export default {
  name: 'CrossDomainTransitionModal',

  props: {
    visible: {
      type: Boolean,
      default: false
    },

    href: {
      type: String,
      default: ''
    },

    postBody: {
      type: Object,
      default: null
    },

    sourceBrand: {
      type: String,
      default: 'rhg',
      validator(value) {
        return ['lhg', 'rhg'].includes(value)
      }
    },

    imageSrc: {
      type: [String, Object],
      default: ''
    },

    imageAlt: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      CROSS_DOMAIN_TRANSITION_MODAL_ID,
      animationInstance: null,
      crossDomainNavigation: null
    }
  },

  created() {
    this.crossDomainNavigation = useCrossDomainNavigation({
      onHideModal: () => {
        this.$emit('close')
      }
    })
    ;[this.originLogo, this.destinationLogo, this.resolvedImageSrc].filter(Boolean).forEach(src => {
      try {
        const img = new Image()
        img.src = src
      } catch (e) {}
    })
  },

  computed: {
    originLogo() {
      return this.sourceBrand === 'lhg' ? LOCAL_LHG_LOGO_URL : LOCAL_RHG_LOGO_URL
    },

    destinationLogo() {
      return this.sourceBrand === 'lhg' ? LOCAL_RHG_LOGO_URL : LOCAL_LHG_LOGO_URL
    },

    linkLabel() {
      return this.translate('weSmartLinkingRedirectionLink')
    },

    titleHtml() {
      return this.translate('weSmartLinkingRedirectionTitle').replace(
        '{0}',
        `<strong>${this.linkLabel}</strong>`
      )
    },

    resolvedImageSrc() {
      const val = this.imageSrc

      if (!val) {
        return ''
      }

      try {
        if (typeof val === 'string') {
          return val
        }

        return buildImageUrl(val, 'Card')
      } catch (e) {
        return ''
      }
    }
  },

  watch: {
    visible(isVisible) {
      if (isVisible) {
        this.$nextTick(() => {
          this.loadArrowsAnimation()
        })

        this.crossDomainNavigation.trigger(this.href, this.postBody)

        return
      }

      this.crossDomainNavigation.cancel()
      this.destroyArrowsAnimation()
    }
  },

  beforeDestroy() {
    this.crossDomainNavigation.cancel()
    this.destroyArrowsAnimation()
  },

  methods: {
    loadArrowsAnimation() {
      if (!this.$refs.arrowsRef || this.animationInstance) {
        return
      }

      try {
        this.animationInstance = lottie.loadAnimation({
          container: this.$refs.arrowsRef,
          renderer: 'svg',
          loop: true,
          autoplay: true,
          animationData
        })
      } catch (error) {
        console.error('CrossDomainTransitionModal: error loading arrows animation', error)
      }
    },

    destroyArrowsAnimation() {
      if (this.animationInstance && this.animationInstance.destroy) {
        this.animationInstance.destroy()
      }

      this.animationInstance = null
    },

    handleConfirm() {
      this.crossDomainNavigation.cancel()
      if (this.href) {
        this.crossDomainNavigation.trigger(this.href, this.postBody, { delayMs: 0 })
      } else {
        this.$emit('confirm')
      }
    }
  }
}
</script>

<style scoped lang="scss">
.cross-domain-transition-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0px;
  background: rgba(0, 0, 0, 0.45);
  @media (min-width: 768px) {
    padding: 32px;
  }
}

.cross-domain-transition-modal {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: var(--color-white);
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.24);

  @media (min-width: 768px) {
    flex-direction: row;
    width: auto;
    max-width: 1000px;
    height: 500px;
    border-radius: 12px;
  }

  &__info {
    display: flex;
    flex: 1;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 40px;
    padding: 32px 20px 20px;

    @media (min-width: 768px) {
      gap: 48px;
      padding: 60px 40px;
    }
  }

  &__logos {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;

    @media (min-width: 768px) {
      gap: 32px;
    }
  }

  &__logo {
    display: block;
    width: auto;
    max-width: 200px;
    min-width: 133px;
    height: 32px;

    @media (min-width: 768px) {
      min-width: 153px;
      height: 37px;
    }
  }

  &__arrows {
    flex-shrink: 0;
    width: 31px;
    height: 31px;
    color: var(--color-eclipse);
    transform: rotate(-90deg);

    @media (min-width: 768px) {
      width: 40px;
      height: 40px;
    }

    ::v-deep path,
    ::v-deep rect,
    ::v-deep polyline {
      fill: currentColor !important;
      stroke: currentColor !important;
    }
  }

  &__text {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    padding: 0 20px;

    @media (min-width: 768px) {
      padding: 0;
    }
  }

  &__title {
    margin: 0;
    color: var(--color-eclipse);
    font-size: 18px;
    line-height: 24px;
    text-align: center;

    @media (min-width: 768px) {
      font-size: 20px;
      line-height: 26px;
    }

    ::v-deep strong {
      font-weight: 700;
    }
  }

  &__subtitle {
    margin: 0;
    color: var(--color-grey-dark);
    font-size: 16px;
    line-height: 22px;
    text-align: center;
  }

  &__image-wrapper {
    flex-shrink: 0;
    width: 100%;
    height: 45vh;
    overflow: hidden;

    @media (min-width: 768px) {
      width: 37%;
      height: 100%;
    }
  }

  &__image {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}
</style>
